// La caja del prompt del modo "Prompt libre" — ADR-2026-09-07-modo-prompt-libre.
//
// Es el composer del asistente (`AsistenteMode.inputBox`) reducido a lo que este modo
// usa: los chips de referencias, el textarea y el mic del dictado. Sin la ruedita
// (Formato y cantidad viven en el pie de la columna), sin Ayuda de IA ni flecha de
// enviar (el Generar es el del pie). La MISMA caja en desktop y en mobile: cambia dónde
// se monta, no lo que es.
//
// Enter genera y Shift+Enter hace salto de línea, como en el asistente: en una pantalla
// cuyo único campo es el prompt, Enter tiene que ser "dale".

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowUp, Image as ImageIcon, Mic, User, X } from 'lucide-react';
import type { ChatAttachment } from '../../agentAttachments';
import { etiquetaDeAdjunto, miniaturaDeAdjunto } from '../../etiquetaDeAdjunto';
import DictadoVoz, { dictadoDisponible } from '../asistente/DictadoVoz';
import { useMenusDeTexto } from '../../../../chat-atlas/ui/useMenusDeTexto';

export interface PromptBoxLibreProps {
  prompt: string;
  onPromptChange: (v: string) => void;
  /** Enter sin Shift. */
  onSubmit: () => void;
  adjuntos: ChatAttachment[];
  onQuitarAdjunto: (id: string) => void;
  /** Hay una imagen subiéndose: chip con spinner al final de la fila. */
  subiendo?: boolean;
  /**
   * Dibuja la fila de chips arriba del textarea. En mobile las referencias ya se ven en
   * la tira de la card "Añadir más", así que ahí se apagan para no mostrarlas dos veces
   * — misma regla que el asistente.
   */
  mostrarChips?: boolean;
  onFocusChange?: (focused: boolean) => void;
  disabled?: boolean;
  /**
   * Controles a la IZQUIERDA de la fila inferior — la ruedita de configuración y el "+"
   * del Creador de video (ADR-2026-09-07-modo-creador-video). Prompt libre no pone
   * ninguno: sus ajustes viven en el pie de la columna.
   */
  izquierda?: React.ReactNode;
  /** Controles a la DERECHA, antes del mic/flecha — la pastilla de créditos. */
  derecha?: React.ReactNode;
  /**
   * Algo FIJO al principio de la fila de adjuntos, del mismo tamaño que un chip: hoy, el
   * tipo de video elegido en el Creador
   * (ADR-2026-09-24-el-tipo-elegido-se-ancla-a-la-caja).
   *
   * No es un adjunto —no se manda como referencia, no sale de `adjuntos` y lo quita su
   * propio control— pero se para en la misma fila porque es lo mismo para el que mira:
   * cosas elegidas que rigen sobre lo que se va a generar. Va PRIMERO, antes de los
   * adjuntos, porque no se acumula: es uno o ninguno.
   *
   * La fila se dibuja si hay esto aunque no haya ningún adjunto, y `mostrarChips` no lo
   * apaga: esa prop es para la pantalla de mobile donde las referencias ya se ven en la
   * tira de la card, y el tipo elegido no está en esa tira.
   */
  anclado?: React.ReactNode;
  /**
   * Dibuja la flecha de enviar cuando hay texto, en el lugar del mic. Es el Generar del
   * Creador de video en desktop, que no tiene pie con botón; Prompt libre genera desde
   * su pie y no la lleva.
   */
  conEnviar?: boolean;
  enviarDeshabilitado?: boolean;
  placeholder?: string;
  /**
   * Cada chip lleva una pastilla con QUÉ es ("Producto", "Imagen", "Modelo"…) —
   * ADR-2026-09-08-los-adjuntos-de-la-caja-dicen-que-son-y-se-abren.
   *
   * Detrás de una prop y no siempre: lo pidió Creador de video, y Prompt libre —el otro
   * dueño de esta caja— no se toca hasta que se decida ahí también.
   */
  conTipo?: boolean;
  /**
   * Tocar un chip lo abre en la vista ampliada. Sin esto la miniatura no es cliqueable,
   * que es como sigue Prompt libre.
   */
  onAbrirAdjunto?: (adjunto: ChatAttachment) => void;
  /**
   * Cómo se dibuja el marco de la caja —
   * ADR-2026-09-11-el-marco-de-la-caja-no-puede-pintarse-afuera.
   *
   * - `'destacado'` (default): borde negro al 6%, sombra y anillo azul. Es la caja del
   *   Creador de video y la de Prompt libre en MOBILE: las dos viven en contenedores
   *   con aire alrededor, así que lo que se pinta afuera de la caja (el anillo y la
   *   sombra) se ve entero.
   * - `'plano'`: TODO el marco adentro del border box — un `border` neutro que pasa a
   *   azul con el foco, sin anillo ni sombra. Es lo que necesita Prompt libre en
   *   DESKTOP: ahí la caja es el primer hijo de un `overflow-y-auto` sin padding, y ese
   *   scroller recorta en su padding box todo lo que se pinte afuera (arriba, izquierda
   *   y derecha se pierden; abajo sobrevive por el `gap`). Un `border` se pinta adentro
   *   y no lo recorta nadie.
   * - `'ninguno'`: la caja no dibuja marco, fondo ni esquinas — quien la monta la
   *   enmarca. Es lo que necesita el modo Redes, cuya caja tiene una columna propia a la
   *   derecha (Producto y Modelo) ADENTRO del mismo marco (ADR-2026-10-01-modo-redes).
   */
  marco?: 'destacado' | 'plano' | 'ninguno';
  /**
   * El mic del dictado va ANTES de lo de `derecha` (a su izquierda) en vez de después. Lo
   * pide Redes, cuyo Generar vive en `derecha` y tiene que quedar último en la fila. Por
   * defecto, como siempre: después.
   */
  micAntesDeDerecha?: boolean;
  /**
   * Los menús del Agente al escribir: `#` abre los colores y `@` menciona un adjunto
   * (ADR-2026-10-04-numeral-y-arroba-en-redes). Lo pide Redes; Prompt libre y el Creador
   * siguen sin ellos hasta que se decida ahí.
   */
  menusDeTexto?: boolean;
  /**
   * Lo que la `@` puede mencionar. Por defecto, `adjuntos` (los chips); Redes pasa la lista
   * ENTERA porque sus productos, modelos e imágenes no van en chips sino en las tarjetas de
   * arriba, y la mención tiene que verlos igual.
   */
  mencionables?: ChatAttachment[];
  /**
   * Cada chip rebota al aparecer (ADR-2026-10-04-rebote-al-anadir-un-adjunto). Lo pide
   * Redes, donde los chips son lo que se suma desde el selector (poses, fondos,
   * creaciones); por defecto, como siempre: sin animación.
   */
  reboteAlAgregar?: boolean;
  /**
   * Cada vez que este número CAMBIA, el texto del prompt hace un rebote sutil: lo usa
   * Redes cuando "Mejorar prompt" termina y reemplaza lo escrito, para que se note que el
   * texto es otro (ADR-2026-10-04-el-prompt-rebota-al-mejorarse). Sin esto, nada.
   */
  pulsoDeTexto?: number;
  /**
   * Un pedido de foco desde afuera: cada vez que el número CAMBIA, el textarea toma el foco
   * con el cursor al final. Lo usan las sugerencias del Asistente, que completan la caja sin
   * mandar (ADR-2026-10-20-las-sugerencias-del-turno-completan-la-caja). Sin esto, nada.
   */
  pedidoDeFoco?: number;
  /**
   * En el teléfono, la fila de abajo se parte en dos: las pastillas de `izquierda` arriba,
   * a todo el ancho, y el mic con `derecha` abajo, a la derecha. Lo pide Crear
   * (ADR-2026-10-09-asistente-en-crear): con la cantidad y el Generar en la misma fila, a
   * las pastillas les quedaban 30 px. En computadora, como siempre.
   */
  filaEnDosEnTelefono?: boolean;
  /** Sin el botón de dictar en el teléfono (Crear: su fila no lo tiene lugar). */
  sinMicEnTelefono?: boolean;
  /**
   * En el teléfono, el textarea arranca más alto (87 px en vez de 43): Crear lo pide para
   * que la caja tenga lugar para escribir (ADR-2026-10-12-la-caja-de-crear-mas-alta-en-el-telefono,
   * ajustado en ADR-2026-10-12-la-caja-de-crear-mas-alta-en-el-telefono-2).
   */
  textoAltoEnTelefono?: boolean;
}

/** Hasta dónde crece el textarea antes de scrollear (px). */
const ALTO_MAX_TEXTAREA = 240;
/** La tipografía del textarea, compartida con la capa espejo que resalta `#colores` y
 *  `@menciones` (tienen que calcar glifo por glifo). El color va acá: con la capa, el
 *  textarea lo pisa con `text-transparent`. */
const CLASES_DEL_TEXTO = 'text-[15.5px] text-brand-black/90 placeholder:text-[#3C3C43]/35 leading-snug';

export const PromptBoxLibre: React.FC<PromptBoxLibreProps> = ({
  prompt,
  onPromptChange,
  onSubmit,
  adjuntos,
  onQuitarAdjunto,
  subiendo = false,
  mostrarChips = true,
  onFocusChange,
  disabled = false,
  izquierda,
  derecha,
  anclado,
  conEnviar = false,
  enviarDeshabilitado = false,
  placeholder = 'Describí lo que querés generar…',
  conTipo = false,
  onAbrirAdjunto,
  marco = 'destacado',
  micAntesDeDerecha = false,
  filaEnDosEnTelefono = false,
  sinMicEnTelefono = false,
  textoAltoEnTelefono = false,
  menusDeTexto = false,
  mencionables,
  reboteAlAgregar = false,
  pulsoDeTexto,
  pedidoDeFoco,
}) => {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  // En `useLayoutEffect`, dentro del gesto del toque: iOS sólo abre el teclado por un
  // `focus()` que venga de uno (el mismo criterio que `BarraEntradaChat`).
  useLayoutEffect(() => {
    if (!pedidoDeFoco) return;
    const el = ref.current;
    if (!el) return;
    el.focus();
    const fin = el.value.length;
    el.setSelectionRange(fin, fin);
  }, [pedidoDeFoco]);
  // El rebote del texto. Con la Web Animations API sobre el envoltorio (textarea + capa
  // espejo juntos) y no con una `key`: remontar el textarea le sacaría el foco y el alto.
  const textoRef = useRef<HTMLDivElement | null>(null);
  const pulsoPrevio = useRef(pulsoDeTexto);
  useEffect(() => {
    if (pulsoPrevio.current === pulsoDeTexto) return;
    pulsoPrevio.current = pulsoDeTexto;
    const el = textoRef.current;
    if (!el || typeof el.animate !== 'function') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    el.animate(
      [
        { transform: 'translateY(6px)', opacity: 0.35 },
        { transform: 'translateY(-2px)', opacity: 1, offset: 0.6 },
        { transform: 'translateY(0)', opacity: 1 },
      ],
      { duration: 420, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
    );
  }, [pulsoDeTexto]);
  // Los menús del `#` y la `@`. El hook corre siempre (es un hook); sin la prop no ve
  // adjuntos y no se le pasan los eventos, así que no abre nada.
  const menus = useMenusDeTexto({
    valor: prompt,
    onCambiarValor: onPromptChange,
    adjuntos: menusDeTexto ? (mencionables ?? adjuntos) : [],
    textareaRef: ref,
  });
  // Dictado por voz: mientras está activo, el contenido de la caja se reemplaza por
  // <DictadoVoz>. Sólo se ofrece con el prompt vacío — el mic ocupa el lugar que en el
  // asistente tiene la flecha muerta.
  const [dictando, setDictando] = useState(false);

  // Autosize (mismo patrón que el PromptBar de la home y el asistente).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, ALTO_MAX_TEXTAREA)}px`;
    // Pasado el tope, el texto scrollea por dentro (como la caja del chat, `BarraEntradaChat`). Con `overflow-hidden`
    // fijo, lo que pasaba del tope quedaba tapado y no había forma de llegar (ADR-2026-10-31-las-cajas-scrollean-pasado-el-tope).
    const desborda = el.scrollHeight > ALTO_MAX_TEXTAREA;
    el.style.overflowY = desborda ? 'auto' : 'hidden';
    // La barra se ve (ADR-2026-10-31-las-cajas-scrollean-pasado-el-tope-2): el campo y la capa espejo que resalta
    // `#colores` y `@menciones` reservan el MISMO lugar para ella (`scrollbar-gutter`), así el texto corta igual en los
    // dos. Sólo mientras desborda: con poco texto no queda un hueco a la derecha.
    const espejo = textoRef.current?.querySelector<HTMLElement>('.espejo-de-textarea');
    for (const n of [el, espejo]) n?.style.setProperty('scrollbar-gutter', desborda ? 'stable' : '');
  }, [prompt, dictando]);

  const hasText = prompt.trim().length > 0;
  const hayChips = mostrarChips && (adjuntos.length > 0 || subiendo);
  const hayFila = hayChips || !!anclado;

  return (
    /* El azul va en hex con alpha (#1644EB es brand-primary) y no con el modificador de
       opacidad de la clase `brand-primary`: esas variantes no compilan en este repo
       (ADR-531). El gris de reposo es `neutral-200`, el mismo de las cards de
       Referencias que van justo abajo. */
    <div
      className={`w-full ${
        marco === 'ninguno'
          ? // Llena el alto que le dé el marco de afuera, con la fila de controles al pie.
            'flex h-full flex-col'
          : marco === 'plano'
            ? 'bg-white rounded-2xl border border-neutral-200 focus-within:border-[rgb(var(--brand-primary-rgb)_/_0.5)] transition-colors'
            : 'bg-white rounded-2xl border border-black/[0.06] shadow-sm ring-1 ring-[rgb(var(--brand-primary-rgb)_/_0.15)]'
      }`}
    >
      {hayFila && (
        <div className="flex flex-wrap items-center gap-2 px-3 pt-3">
          {anclado}
          {hayChips && adjuntos.map((a) => {
            const thumb =
              miniaturaDeAdjunto(a);
            const miniatura = thumb ? (
              <img
                src={thumb}
                alt={a.kind === 'image' ? 'Imagen' : a.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="w-full h-full inline-flex items-center justify-center text-brand-primary">
                {a.kind === 'model' ? (
                  <User className="w-6 h-6" />
                ) : (
                  <ImageIcon className="w-6 h-6" />
                )}
              </span>
            );
            return (
              <div
                key={a.id}
                // Con rótulo el chip crece a 76px: "Producto" a 10.5px no entra legible
                // en 64. Sin rótulo queda como estaba.
                className={`group relative rounded-xl overflow-hidden bg-neutral-100 border border-black/[0.06] ${
                  conTipo ? 'w-[76px] h-[76px]' : 'w-16 h-16'
                } ${reboteAlAgregar ? 'animate-rebote-de-adjunto' : ''}`}
              >
                {/* El contenedor sigue siendo un `div` y la miniatura es un botón
                    `absolute inset-0`: la X tiene que ser su HERMANA y no su hija, que
                    sería un botón dentro de otro (HTML inválido). Es el mismo armado que
                    las cards de la columna de resultados. */}
                {onAbrirAdjunto ? (
                  <button
                    type="button"
                    onClick={() => onAbrirAdjunto(a)}
                    aria-label={`Ver ${a.kind === 'image' ? 'imagen' : a.name}`}
                    className="absolute inset-0 transition-transform active:scale-[0.97]"
                  >
                    {miniatura}
                  </button>
                ) : (
                  miniatura
                )}
                {/* La pastilla con QUÉ es, abajo sobre un degradé —
                    ADR-2026-09-08-los-adjuntos-de-la-caja-dicen-que-son-y-se-abren.
                    Abajo y no arriba porque arriba a la derecha vive la X, y un rótulo
                    de ancho completo la chocaría. Es la misma anatomía que las cards de
                    la columna de resultados: el dato sobre la foto, en la base. */}
                {conTipo && (
                  <span className="pointer-events-none absolute inset-x-0 bottom-0 px-1.5 pt-5 pb-1 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                    <span className="block truncate text-center text-[10.5px] font-semibold leading-tight text-white">
                      {etiquetaDeAdjunto(a)}
                    </span>
                  </span>
                )}
                {/* La X adentro de la miniatura. Mobile: siempre; desktop: al hover. */}
                <button
                  type="button"
                  onClick={() => onQuitarAdjunto(a.id)}
                  aria-label="Quitar referencia"
                  className="absolute top-1 right-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-black/55 hover:bg-black/75 text-white shadow-sm transition-opacity opacity-100 md:opacity-0 md:group-hover:opacity-100"
                >
                  <X className="w-3 h-3" strokeWidth={2.5} />
                </button>
              </div>
            );
          })}
          {hayChips && subiendo && (
            <div
              className={`rounded-xl bg-neutral-100 border border-black/[0.06] inline-flex items-center justify-center ${
                conTipo ? 'w-[76px] h-[76px]' : 'w-16 h-16'
              }`}
            >
              <span className="w-4 h-4 rounded-full border-2 border-[rgb(var(--brand-primary-rgb)_/_0.3)] border-t-brand-primary animate-spin" />
            </div>
          )}
        </div>
      )}

      {dictando ? (
        // Con marco 'ninguno' la caja tiene un alto mínimo: el dictado lo llena, con la
        // transcripción arriba (donde está el prompt) y su fila de X / confirmar al PIE,
        // donde estaba el mic que se tocó.
        <div className={marco === 'ninguno' ? 'flex flex-1 flex-col' : ''}>
          <DictadoVoz
            llenarAlto={marco === 'ninguno'}
            onCancel={() => setDictando(false)}
            onConfirm={(t) => {
              setDictando(false);
              onPromptChange(t);
              // Foco al textarea ya remontado, con el texto listo para retocar.
              window.setTimeout(() => ref.current?.focus(), 0);
            }}
          />
        </div>
      ) : (
        <>
          {/* `relative`: los menús del `#` y la `@` se apoyan sobre el borde de arriba del
              textarea (`absolute bottom-full`). */}
          <div ref={textoRef} className={`relative px-5 pt-4 ${marco === 'ninguno' ? 'flex-1' : ''}`}>
            {/* Con los menús, detrás del textarea va la capa espejo que resalta `#colores`
                y `@menciones`, y el texto del textarea pasa a transparente (el cursor y la
                selección siguen siendo suyos). El `inset-0` del espejo es el de ESTE
                envoltorio, que ya tiene el padding; el textarea va `relative` encima. */}
            {menusDeTexto && menus.resaltado(`${CLASES_DEL_TEXTO} px-5 pt-4`)}
            <textarea
              ref={ref}
              value={prompt}
              onScroll={menusDeTexto ? menus.alScrollear : undefined}
              onChange={(e) => {
                onPromptChange(e.target.value);
                if (menusDeTexto) menus.releerToken(e.target);
              }}
              onSelect={(e) => {
                if (menusDeTexto) menus.releerToken(e.currentTarget);
              }}
              onFocus={() => onFocusChange?.(true)}
              onBlur={(e) => {
                if (menusDeTexto) menus.alDesenfocar(e);
                onFocusChange?.(false);
              }}
              onKeyDown={(e) => {
                if (menusDeTexto && menus.alTeclear(e)) return;
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  onSubmit();
                }
              }}
              placeholder={placeholder}
              rows={1}
              disabled={disabled}
              // Dos líneas de piso: la caja arranca con aire para escribir. Va como
              // min-height y no como `rows` porque el autosize escribe `style.height`
              // inline y el mínimo tiene que ganarle.
              className={`relative w-full resize-none bg-transparent border-none outline-none overflow-y-hidden disabled:cursor-not-allowed min-h-[43px] ${textoAltoEnTelefono ? 'max-md:min-h-[87px]' : ''} ${CLASES_DEL_TEXTO} ${
                menusDeTexto ? 'text-transparent caret-brand-black' : ''
              }`}
            />
            {menusDeTexto && menus.menus}
          </div>
          {/* Fila inferior: [izquierda] … [derecha] [mic | flecha]. El mic sólo mientras
              no hay texto (el dictado reemplaza al prompt, no lo completa); con texto, la
              flecha si el modo la pide. Sin nada que dibujar la fila queda de aire, para
              que la caja mida igual.
              OJO: NADA de `overflow-*` acá — la fila contiene los popovers de la
              izquierda (ruedita, "+") y un overflow los recortaría.

              `pt-5` (20px) y no `pt-1`: una LÍNEA de aire entre el prompt y los
              controles. Con 4px la última línea de texto quedaba pegada a la ruedita y
              los dos bloques se leían como uno solo; 20px es la altura de línea del
              textarea (15.5px × `leading-snug`), así que el aire es el mismo salto que
              hay entre dos renglones del prompt. */}
          <div className={`flex items-center gap-2 px-3 pb-3 pt-5 min-h-[48px] ${filaEnDosEnTelefono ? 'max-md:flex-wrap max-md:gap-y-2.5 max-md:pt-3' : ''}`}>
            {/* El grupo izquierdo SCROLLEA en horizontal cuando sus pastillas no entran
                (ADR-2026-09-20-la-fila-del-composer-scrollea-y-sus-menus-se-portalean):
                con la ruedita, "Añadir", "Recientes" y un tipo de video de nombre largo,
                la fila se desbordaba y empujaba fuera de la caja los créditos y el botón
                de enviar. Así lo de la derecha queda SIEMPRE a la vista y lo que se
                recorre es lo de la izquierda.

                `py-1 -my-1` da 4px de aire vertical dentro del área que el scroll recorta,
                para que un anillo o una sombra de pastilla no salga cortada; no cambia el
                alto de la fila. Cada pastilla trae su `shrink-0`, así se recorren enteras
                en vez de apretarse. Los menús de "Configuración" y "Añadir" están
                portaleados justamente porque este `overflow` los recortaría. */}
            <div className={`min-w-0 flex-1 flex items-center gap-2 overflow-x-auto scrollbar-hide py-1 -my-1 ${filaEnDosEnTelefono ? 'max-md:basis-full' : ''}`}>
              {izquierda}
            </div>
            {/* En dos filas, la de abajo (el botón de la acción) va a todo el ancho
                (ADR-2026-10-13-generar-dentro-de-la-caja-en-el-telefono). */}
            <div className={`shrink-0 flex items-center gap-2 ${filaEnDosEnTelefono ? 'max-md:w-full' : ''}`}>
              {(() => {
                const mic =
                  !hasText && dictadoDisponible() && !disabled ? (
                    <button
                      type="button"
                      onClick={() => {
                        menus.cerrar();
                        setDictando(true);
                      }}
                      aria-label="Dictar por voz"
                      className={`shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 text-[rgb(var(--brand-black-rgb)_/_0.7)] transition-colors ${sinMicEnTelefono ? 'max-md:hidden' : ''}`}
                    >
                      <Mic className="w-4 h-4" strokeWidth={2.25} />
                    </button>
                  ) : null;
                return micAntesDeDerecha ? (
                  <>
                    {mic}
                    {derecha}
                  </>
                ) : (
                  <>
                    {derecha}
                    {mic}
                  </>
                );
              })()}
              {/* La flecha aparece con texto, o cuando el modo dice que igual se puede
                  enviar (`enviarDeshabilitado` en falso): el Creador de video genera con
                  un tipo elegido o un adjunto y la caja vacía, y sin esto no tenía botón
                  (ADR-2026-09-19-el-idioma-es-una-direccion-no-una-linea-hablada). Prompt
                  libre no cambia: su regla exige texto, así que sin texto la flecha sigue
                  sin aparecer. */}
              {conEnviar && (hasText || !enviarDeshabilitado) && (
                <button
                  type="button"
                  onClick={enviarDeshabilitado ? undefined : onSubmit}
                  disabled={enviarDeshabilitado || disabled}
                  aria-label="Generar"
                  className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full shadow-sm bg-brand-primary hover:bg-brand-primary-hover text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ArrowUp className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PromptBoxLibre;
